<template>
  <div>
    <v-dialog v-model="dialog">
      <v-card>
        <v-card-title>
          <v-col class="text-right px-4">
            <v-btn icon @click="dialog=false">
              <v-icon large color="primary">close</v-icon>
            </v-btn>
          </v-col>
        </v-card-title>

        <v-card-text>
          <v-tabs
              color="primary"
              centered
              ref="tabs"
              v-model="tab"
              background-color="grey lighten-4"
          >
            <v-tab to="#base">
              <v-icon class="mr-3">home</v-icon>
              {{ $t('components:external-facility-dialog.basic') }}
            </v-tab>
            <v-tab to="#tech" v-if="overview.technicalInfo">
              <v-icon class="mr-3">build</v-icon>
              {{ $t('components:external-facility-dialog.technic') }}
            </v-tab>
            <v-tab to="#docu" v-if="overview.facilityDocumentations">
              <v-icon class="mr-3">description</v-icon>
              {{ $t('common:documentation_plural') }}
            </v-tab>
            <v-tab to="#contact" v-if="overview.facilityUserAssignments">
              <v-icon class="mr-3">engineering</v-icon>
              {{ $t('components:external-facility-dialog.contact') }}
            </v-tab>
          </v-tabs>

          <v-tabs-items v-model="tab">
            <v-tab-item value="base">
              <v-row justify="space-around">
                <v-col cols="12" md="5">
                  <h5 class="font-weight-bold">{{ $t('common:building') }}</h5>
                  <LabelField
                      :label="$t('common:name')"
                      :value="overview.facility.buildingName"
                  />

                  <LabelField
                      :label="$t('common:owner')"
                      :value="overview.ownerName"
                  />

                  <LabelField
                      :label="$t('common:operator')"
                      :value="overview.operatorName"
                  />

                  <LabelField
                      :label="this.$t('common:street')"
                      :value="overview.facility.buildingStreet"
                  />

                  <LabelField
                      :label="this.$t('common:zip')"
                      :value="overview.facility.buildingZip"
                  />

                  <LabelField
                      :label="this.$t('common:city')"
                      :value="overview.facility.buildingCity"
                  />

                  <LabelField
                      label="Building province"
                      :value="overview.translatedProvinceName"
                  />
                  <LabelField
                      label="Building country"
                      :value="overview.translatedCountryName"
                  />

                  <OpenStreetMap
                      ref="map"
                      :lat="overview.facility.buildingLatitude"
                      :long="overview.facility.buildingLongitude"
                      height="250"
                  />
                </v-col>
                <v-col cols="12" md="5">
                  <h5 class="font-weight-bold">{{ $t('common:facility') }}</h5>
                  <LabelField
                      :label="this.$t('common:name')"
                      :value="overview.facility.name"
                  />

                  <LabelField
                      :label="this.$t('common:facility-number')"
                      :value="overview.facility.objectNumber"
                  />

                  <LabelField
                      :label="this.$t('common:type')"
                      :value="overview.facility.typeName"
                  />

                  <LabelField
                      label="Stiege"
                      :value="overview.facility.stairway"
                  />

                  <LabelField
                      label="Manager"
                      :value="overview.managerName"
                  />
                </v-col>
              </v-row>
            </v-tab-item>
            <v-tab-item value="tech" v-if="overview.technicalInfo">
              <v-row justify="space-around">
                <v-col cols="12" md="5">
                  <!-- Typ of Facility Section -->
                  <h4 class="primary--text font-weight-medium">{{ $t('common:type') }}</h4>
                  <LabelField
                      :label="$t('book/technical-info:labels.elevator-type')"
                      :value="overview.technicalInfo.type ? $t('enums:technical.elevator-type.' + overview.technicalInfo.type) : ''"
                  />

                  <v-checkbox
                      v-model="overview.technicalInfo.firefighterLift"
                      :label="$t('book/technical-info:labels.firefighters-lift')"
                      disabled
                  />

                  <h4 class="primary--text mb-5">{{ $t('book/technical-info:titles.connected-facilities') }}</h4>
                  <FacilityConnectionView
                      v-model="overview.technicalInfo.connectedFacilities"
                      :technical-info="overview.technicalInfo"
                      :selectableFacilities="[]"
                      view-only
                  />

                  <!-- Transport Section  -->
                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/technical-info:titles.transport') }}</h4>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <v-checkbox
                          v-model="overview.technicalInfo.transportPerson"
                          :label="$t('book/technical-info:labels.transport-person')"
                          disabled
                      />
                      <v-checkbox
                          v-model="overview.technicalInfo.transportFreight"
                          :label="$t('book/technical-info:labels.transport-freight')"
                          disabled
                      />
                      <v-checkbox
                          v-model="overview.technicalInfo.transportGoods"
                          :label="$t('book/technical-info:labels.transport-goods')"
                          disabled
                      />
                    </v-col>
                    <v-col cols="6" class="py-0">
                      <v-checkbox
                          v-model="overview.technicalInfo.transportCar"
                          :label="$t('book/technical-info:labels.transport-car')"
                          disabled
                      />
                      <v-checkbox
                          v-model="overview.technicalInfo.transportBed"
                          :label="$t('book/technical-info:labels.transport-bed')"
                          disabled
                      />
                    </v-col>
                  </v-row>

                  <!-- Story Section -->
                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/technical-info:titles.story') }}</h4>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('book/technical-info:labels.lane-start')"
                          :value="overview.technicalInfo.laneStart"
                      />
                    </v-col>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('book/technical-info:labels.lane-end')"
                          :value="overview.technicalInfo.laneEnd"
                      />
                    </v-col>
                  </v-row>

                  <LabelField
                      :label="$t('book/technical-info:labels.stations')"
                      :value="overview.technicalInfo.stations"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.loading-stations')"
                      :value="overview.technicalInfo.loadingStations"
                  />

                  <!-- Key-Safe Section -->
                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/technical-info:titles.key-safe') }}</h4>
                  <v-checkbox
                      v-model="overview.technicalInfo.keySafeExists"
                      :label="$t('book/technical-info:labels.key-safe-exists')"
                      disabled
                  />

                  <LabelField
                      v-if="overview.technicalInfo.keySafeExists"
                      :label="$t('book/technical-info:labels.key-safe-info')"
                      :value="overview.technicalInfo.keySafeInfo"
                  />

                  <!-- Engine Room Section -->
                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/technical-info:titles.engine') }}</h4>
                  <v-checkbox
                      v-model="overview.technicalInfo.engineRoomExists"
                      :label="$t('book/technical-info:labels.engine-room-exists')"
                      disabled
                  />

                  <LabelField
                      v-if="overview.technicalInfo.engineRoomExists"
                      :label="$t('book/technical-info:labels.engine-room-position')"
                      :value="overview.technicalInfo.engineRoomPosition ?
                    $t('enums:technical.engine-room-position.' + overview.technicalInfo.engineRoomPosition) : ''"
                  />

                  <LabelField
                      v-if="overview.technicalInfo.engineRoomExists"
                      :label="$t('book/technical-info:labels.engine-room-position-info')"
                      :value="overview.technicalInfo.engineRoomPositionInfo"
                  />

                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/technical-info:titles.cabin') }}</h4>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('book/technical-info:labels.cabin-width')"
                          :value="overview.technicalInfo.cabinWidth + ' mm'"
                      />
                    </v-col>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('book/technical-info:labels.cabin-depth')"
                          :value="overview.technicalInfo.cabinDepth + ' mm'"
                      />
                    </v-col>
                  </v-row>

                  <h6 class="primary--text font-weight-medium my-1">{{ $t('book/technical-info:titles.cabin-door') }}</h6>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('common:type')"
                          :value="overview.technicalInfo.cabinDoorType ? $t('enums:technical.elevator-door-types.' + overview.technicalInfo.cabinDoorType) : ''"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('book/technical-info:labels.cabin-door-width')"
                          :value="overview.technicalInfo.cabinDoorWidth + ' mm'"
                      />
                    </v-col>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('book/technical-info:labels.cabin-door-height')"
                          :value="overview.technicalInfo.cabinDoorHeight + ' mm'"
                      />
                    </v-col>
                  </v-row>

                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/technical-info:titles.shaft') }}</h4>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('book/technical-info:labels.shaft-protection-type')"
                          :value="overview.technicalInfo.shaftProtectionType ? $t('enums:technical.elevator-protection-types.' + overview.technicalInfo.shaftProtectionType) : ''"
                      />
                    </v-col>
                  </v-row>

                  <h6 class="primary--text font-weight-medium my-1">{{ $t('book/technical-info:titles.shaft-door') }}</h6>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('common:type')"
                          :value="overview.technicalInfo.shaftDoorType ? $t('enums:technical.elevator-door-types.' + overview.technicalInfo.shaftDoorType) : ''"
                      />
                    </v-col>
                  </v-row>
                  <v-row>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('book/technical-info:labels.shaft-door-width')"
                          :value="overview.technicalInfo.shaftDoorWidth + ' mm'"
                      />
                    </v-col>
                    <v-col cols="6" class="py-0">
                      <LabelField
                          :label="$t('book/technical-info:labels.shaft-door-height')"
                          :value="overview.technicalInfo.shaftDoorHeight + ' mm'"
                      />
                    </v-col>
                  </v-row>
                </v-col>

                <v-col cols="12" md="5">
                  <!-- Technical informations Section -->
                  <h4 class="primary--text font-weight-medium">{{ $t('book/technical-info:titles.tech') }}</h4>
                  <LabelField
                      :label="$t('book/technical-info:labels.drive')"
                      :value="overview.technicalInfo.drive ? $t('enums:technical.drive.' + overview.technicalInfo.drive) : ''"
                  />

                  <LabelField
                      v-if="overview.technicalInfo.drive === 'HYDRAULIC'"
                      :label="$t('book/technical-info:labels.hydraulic-drive')"
                      :value="overview.technicalInfo.hydraulicDriveType ? $t('enums:technical.elevator-hydraulic-drive-types.' + overview.technicalInfo.hydraulicDriveType) : ''"
                  />

                  <div v-if="overview.technicalInfo.drive === 'ROPE'">
                    <p class="font-weight-bold mt-5">{{ $t('book/technical-info:labels.rope-drive') }}:</p>
                    <ul>
                      <li v-for="ropeDrive in overview.technicalInfo.ropeDriveTypes" :key="ropeDrive">
                        <p class="pb-0">
                          {{ $t('enums:technical.elevator-rope-drive-types.' + ropeDrive) }}
                        </p>
                      </li>
                    </ul>
                  </div>

                  <LabelField
                      :label="$t('book/technical-info:labels.door-position')"
                      :value="overview.technicalInfo.doorPosition ?
                    $t('enums:technical.elevator-door-position.' + overview.technicalInfo.doorPosition) : ''"
                  />


                  <LabelField
                      :label="$t('book/technical-info:labels.max-load')"
                      :value="overview.technicalInfo.maxLoad"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.max-persons')"
                      :value="overview.technicalInfo.maxPersons"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.operating-speed')"
                      :value="overview.technicalInfo.operatingSpeed"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.cabin-height')"
                      :value="overview.technicalInfo.cabinHeight"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.controls')"
                      :value="overview.technicalInfo.control"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.frequency-converter')"
                      :value="overview.technicalInfo.frequencyConverter"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.door-security')"
                      :value="overview.technicalInfo.doorSecurityType ? $t('enums:technical.elevator-door-security-types.' + overview.technicalInfo.doorSecurityType) : ''"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.manufacturer')"
                      :value="overview.manufacturerName"
                  />

                  <v-checkbox
                      v-model="overview.technicalInfo.awmModule"
                      :label="$t('book/technical-info:labels.AWM-module')"
                      disabled
                  />

                  <v-checkbox
                      v-model="overview.technicalInfo.knownOperator"
                      :label="$t('book/technical-info:labels.known-operator')"
                      disabled
                  />

                  <!-- Installation -->
                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/technical-info:titles.installation') }}</h4>

                  <LabelField
                      :label="$t('book/technical-info:labels.build-year')"
                      :value="overview.technicalInfo.buildYear"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.building-permit')"
                      :value="overview.technicalInfo.buildingPermit"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.permission-date')"
                      :value="overview.technicalInfo.permissionToUse"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.date-modernized')"
                      :value="overview.technicalInfo.modernizedOn"
                  />

                  <LabelField
                      :label="$t('book/technical-info:labels.assembler')"
                      :value="overview.assemblerName"
                  />

                  <!-- Emergency Section -->
                  <h4 class="primary--text font-weight-medium mt-5">{{ $t('book/technical-info:titles.emergency') }}</h4>
                  <ul>
                    <li v-for="emergency in overview.technicalInfo.emergencyType" :key="emergency">
                      <p class="pb-0">
                        {{ $t('enums:technical.elevator-emergency-types.' + emergency) }}
                      </p>
                    </li>
                  </ul>
                  <v-checkbox
                      v-model="overview.technicalInfo.emergencyCallSystemGSM"
                      :label="$t('book/technical-info:labels.emergency-GSM')"
                      disabled
                  />

                  <v-checkbox
                      v-if="overview.technicalInfo.emergencyCallSystemGSM"
                      v-model="overview.technicalInfo.emergencyAnalog"
                      :label="$t('book/technical-info:labels.emergency-analog')"
                      disabled
                  />

                  <LabelField
                      v-if="overview.technicalInfo.emergencyCallSystemGSM"
                      :label="$t('book/technical-info:labels.emergency-phone')"
                      :value="overview.technicalInfo.emergencyPhoneNumber"
                  />

                  <LabelField
                      v-if="overview.technicalInfo.emergencyCallSystemGSM"
                      :label="$t('book/technical-info:labels.emergency-info')"
                      :value="overview.technicalInfo.emergencyCallSystemInfo"
                  />
                </v-col>
              </v-row>
            </v-tab-item>
            <v-tab-item value="docu" v-if="overview.facilityDocumentations">
              <DataTable
                  :headers="docuHeaders"
                  :data="overview.facilityDocumentations"
                  icon="visibility"
                  icon2="cloud_download"
                  :iconTooltip="$t('buttons:show')"
                  :iconTooltip2="$t('buttons:download')"
                  @clickedButton1="viewDocumentation"
                  @clickedButton2="downloadDocumentation"
                  sortBy="translatedType"
              />
            </v-tab-item>
            <v-tab-item value="contact" v-if="overview.facilityUserAssignments">
              <DataTable
                  :headers="contactHeader"
                  :data="overview.facilityUserAssignments"
                  icon="phone"
                  icon2="email"
                  :iconTooltip="$t('buttons:call')"
                  :iconTooltip2="$t('buttons:send-email')"
                  @clickedButton1="callPhoneNumber"
                  @clickedButton2="writeEmail"
                  sort-by="userName"
              />
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
      </v-card>

      <DocumentViewer
          hide-overlay
          ref="documentViewer"/>
    </v-dialog>
  </div>
</template>

<script>
import FacilityService from "@/modules/book/services/facility-service";
import DataUtils from "@/modules/core/services/data-utils";
import FacilityOverview from "@/modules/book/data/facility-overview";
import LabelField from "@/modules/core/components/LabelField";
import OpenStreetMap from "@/modules/core/components/OpenStreetMap/OpenStreetMap";
import Utils from "@/modules/core/services/utils";
import DataTable from "@/modules/core/components/DataTable/DataTable";
import DocumentationService from "@/modules/core/services/documentation-service";
import DocumentViewer from "@/modules/core/components/DocumentViewer/DocumentViewer";
import FacilityConnectionView from "@/modules/book/components/Facility/FacilityConnectionView";

export default {
  name: "ExternalFacilityDialog",
  components: {DocumentViewer, DataTable, OpenStreetMap, LabelField, FacilityConnectionView},
  data() {
    return {
      dialog: false,
      tab: '#base',
      overview: new FacilityOverview()
    }
  },
  computed: {
    docuHeaders() {
      return [
        {text: this.$t('common:type'), value: 'translatedType'},
        {text: this.$t('common:document-name'), value: 'name'},
        {text: '', value: 'controls'}
      ]
    },
    contactHeader() {
      return [
        {text: this.$t('common:role'), value: 'roleName'},
        {text: this.$t('common:name'), value: 'userName'},
        {text: this.$t('common:email'), value: 'userEmail'},
        {text: this.$t('common:phone'), value: 'userPhone'},
        {text: '', value: 'controls'}
      ]
    }
  },
  methods: {
    initDialog(facilityId) {
      FacilityService.readForWorker(facilityId,
          (res) => {
            this.overview = DataUtils.parseDataToSingleObject(res.data, FacilityOverview);
            this.overview.translatedProvinceName = Utils.translateProvinces(this.overview.countryName, this.overview.provinceName);
            this.overview.translatedCountryName = Utils.translateCountry(this.overview.countryName);

            if (this.tab === 'base' && this.overview.facility.buildingLatitude && this.overview.facility.buildingLongitude) {
              this.$refs.map.recenterMap(this.overview.facility.buildingLatitude, this.overview.facility.buildingLongitude);
              this.$refs.map.refreshMap();
            }

            this.overview.facilityDocumentations.forEach((docu) => {
              docu.translatedType = this.$t('enums:facility-documentation-types.' + docu.type);
              docu.src = '';
            });

            this.dialog = true;
          },
          (error) => {
            this.$logger.error("Could not load worker facility data, because: " + error);
            Utils.showFailureSnackbar(this.$t('snackbar:load-failure', {name: this.$t('components:external-facility-dialog.facility-data')}))
          }
      )
    },

    viewDocumentation(item) {
      this.$refs.documentViewer.initViewerById(this.overview.facilityDocumentations, item.id, item.facilityId, item.documentType);
    },

    downloadDocumentation(item) {
      DocumentationService.downloadFile(item.id, item.fileName, item.fileType, item.facilityId, item.documentType);
    },

    callPhoneNumber(item) {
      window.open('tel:' + item.userPhone)
    },

    writeEmail(item) {
      window.open('mailto:' + item.userEmail)
    },
  }
}
</script>

<style scoped>

</style>